import { Dropdown } from '@legion-ui/core';
import { useNavigate } from 'react-router-dom';
import { MdKeyboardArrowDown, MdMenu } from 'react-icons/md';

export type HeaderBarType = {
  imageUrl: string;
  avatarUrl?: string;
  username?: string;
  userRole?: string;
  dropdownOption: {
    title: string;
    href: string;
  }[];
  onLogoutHandler?: () => void;
  onMenuClick: () => void;
  onToggleCollapse?: () => void;
};

export const HeaderBar = ({
  imageUrl,
  avatarUrl,
  dropdownOption,
  username,
  userRole,
  onLogoutHandler,
  onMenuClick,
  onToggleCollapse,
}: HeaderBarType) => {
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-xl border-b border-slate-700/50">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-blue-400/10"></div>
      
      <div className="relative flex px-6 justify-between py-3 items-center">
        {/* Left Section - Hamburger + Mobile Menu + Logo */}
        <div className="flex items-center space-x-4">
          {/* Desktop Hamburger Button for Sidebar Toggle */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex p-2 rounded-xl hover:bg-white/10 transition-all duration-200"
            title="Toggle Sidebar"
          >
            <MdMenu size={24} className="text-blue-300 hover:text-white" />
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-xl hover:bg-white/10 transition-all duration-200"
            data-testid="menu-icon"
          >
            <MdMenu size={24} className="text-blue-300 hover:text-white" />
          </button>

          {/* Logo with white background for visibility */}
          <div className="flex items-center bg-white rounded-lg px-3 py-1.5 shadow-md">
            <img
              src={imageUrl}
              alt="Apilogy Logo"
              className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity duration-200"
              onClick={() => navigate('/users')}
            />
          </div>

          {/* Title */}
          <div className="hidden md:block">
            <h1 className="text-xl font-bold text-white tracking-tight">Admin Portal</h1>
            <p className="text-sm text-blue-300">Apilogy Management</p>
          </div>
        </div>

        {/* Right Section - User Profile */}
        <div className="flex items-center space-x-3">
          <img
            src={avatarUrl ? avatarUrl : '/assets/user.png'}
            alt="avatar"
            crossOrigin="anonymous"
            className="object-cover w-10 h-10 rounded-full border-2 border-blue-600 shadow-md"
          />
          <div className="hidden md:flex flex-col">
            <p className="font-semibold text-white text-sm">{username}</p>
            <p className="text-xs text-blue-300">{userRole}</p>
          </div>
          <Dropdown
            trigger={
              <button className="flex items-center p-2 rounded-lg hover:bg-white/10 transition-all duration-200">
                <MdKeyboardArrowDown size={20} className="text-blue-300 hover:text-white" />
              </button>
            }
            onClose={() => {}}
          >
            <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 py-2 min-w-[150px]">
              {dropdownOption.map((dropdown, i) => (
                <div key={i} className="px-1">
                  <button
                    type="button"
                    onClick={
                      dropdown.href.toLowerCase().includes('logout')
                        ? onLogoutHandler
                        : () => (window.location.href = dropdown.href)
                    }
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg transition-colors duration-200"
                  >
                    {dropdown.title}
                  </button>
                </div>
              ))}
            </div>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
